import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dmsApi } from '../../api/dms.js';

export default function Library() {
  const [tree, setTree] = useState([]);
  const [folderId, setFolderId] = useState(null); // null = root
  const [docs, setDocs] = useState([]);
  const [view, setView] = useState('grid');
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [drag, setDrag] = useState(false);
  const nav = useNavigate();

  const refreshTree = () => dmsApi.folderTree().then((d) => setTree(d?.tree || []));
  const refreshDocs = useCallback(() => {
    dmsApi.documents({ folder: folderId === null ? 'null' : folderId, search }).then((d) => setDocs(d?.items || []));
  }, [folderId, search]);

  useEffect(() => { refreshTree(); }, []);
  useEffect(() => { refreshDocs(); }, [refreshDocs]);

  async function uploadFiles(files) {
    setError(null);
    for (const file of files) {
      try {
        await dmsApi.upload(file, { folderId });
      } catch (err) {
        setError(err.response?.data?.message || 'Upload failed');
      }
    }
    refreshDocs();
  }

  function onDrop(e) {
    e.preventDefault();
    setDrag(false);
    const files = [...e.dataTransfer.files];
    if (files.length) uploadFiles(files);
  }

  async function newFolder() {
    const name = window.prompt('Folder name?');
    if (!name) return;
    try {
      await dmsApi.createFolder({ name, parentId: folderId || null });
      refreshTree();
    } catch (err) {
      setError(err.response?.data?.message || 'Create failed');
    }
  }

  return (
    <div className="dms-layout">
      <aside className="dms-tree">
        <header className="page-header" style={{ padding: '12px' }}>
          <h3>Library</h3>
          <button className="btn ghost small" onClick={newFolder}>+ Folder</button>
        </header>
        <ul>
          <li className={folderId === null ? 'active' : ''} onClick={() => setFolderId(null)}>📁 Root</li>
          {tree.map((root) => (
            <Folder key={root.id} node={root} depth={0} folderId={folderId} setFolderId={setFolderId} />
          ))}
        </ul>
      </aside>

      <section
        className={`dms-main ${drag ? 'dropping' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
      >
        <header className="page-header">
          <div>
            <h1>Documents</h1>
            <p className="muted small">{docs.length} item(s) · drag-and-drop files to upload</p>
          </div>
          <div className="actions">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" />
            <div className="seg">
              <button className={view === 'grid' ? 'active' : ''} onClick={() => setView('grid')}>Grid</button>
              <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>List</button>
            </div>
            <label className="btn primary">
              + Upload
              <input type="file" multiple style={{ display: 'none' }} onChange={(e) => uploadFiles([...e.target.files])} />
            </label>
          </div>
        </header>

        {error && <div className="alert error">{error}</div>}

        {view === 'grid' ? (
          <div className="dms-grid">
            {docs.map((d) => (
              <button key={d.id} className="dms-card" onClick={() => nav(`/dms/${d.id}`)}>
                <div className="dms-icon">{iconFor(d.mime_type)}</div>
                <div className="name" title={d.name}>{d.name}</div>
                <div className="muted small">v{d.version} · {formatSize(d.size)}</div>
                <span className={`chip status-${d.status === 'approved' ? 'approved' : d.status === 'in_review' ? 'pending' : 'cancelled'}`}>{d.status}</span>
              </button>
            ))}
            {docs.length === 0 && <div className="muted center">Drop files anywhere or use Upload.</div>}
          </div>
        ) : (
          <div className="card">
            <table className="data-table">
              <thead><tr><th>Name</th><th>Folder</th><th>Version</th><th>Size</th><th>Owner</th><th>Status</th></tr></thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} onClick={() => nav(`/dms/${d.id}`)} style={{ cursor: 'pointer' }}>
                    <td><strong>{d.name}</strong></td>
                    <td className="muted">{d.folder_name || '/'}</td>
                    <td>v{d.version}</td>
                    <td className="muted">{formatSize(d.size)}</td>
                    <td className="muted">{d.owner_name || '—'}</td>
                    <td><span className={`chip status-${d.status === 'approved' ? 'approved' : d.status === 'in_review' ? 'pending' : 'cancelled'}`}>{d.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Folder({ node, depth, folderId, setFolderId }) {
  const [open, setOpen] = useState(true);
  const has = node.children && node.children.length > 0;
  return (
    <>
      <li
        style={{ paddingLeft: 12 + depth * 14 }}
        className={folderId === node.id ? 'active' : ''}
        onClick={() => setFolderId(node.id)}
      >
        {has && <span onClick={(e) => { e.stopPropagation(); setOpen(!open); }} style={{ marginRight: 4 }}>{open ? '▾' : '▸'}</span>}
        📁 {node.name}
      </li>
      {open && has && node.children.map((c) => (
        <Folder key={c.id} node={c} depth={depth + 1} folderId={folderId} setFolderId={setFolderId} />
      ))}
    </>
  );
}

function iconFor(mime) {
  if (!mime) return '📄';
  if (mime.startsWith('image/')) return '🖼️';
  if (mime.includes('pdf')) return '📕';
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv')) return '📊';
  if (mime.includes('word') || mime.includes('document')) return '📝';
  return '📄';
}

function formatSize(b) {
  const n = Number(b || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
